// DLG-128 Create / Edit Account (legacy pages/shared/accounts/AccountsItem.vue; field note backoffice.accounts.list,
// shots `.list-create-account`, `.list-edit-account`), opened from the Accounts list's Create Account button and the
// account card's Edit account. Select Reseller (Radish admins only), Business Name, ACH API Key, Email, Phone; Cancel
// and Create or Update. Legacy's copy and messages, with one change (A39 (b), Andrew 2026-10-12; ledger IMPROVE): the
// ACH API Key is optional and write-only. Edit leaves it blank as legacy did, and says which key is stored by its last
// four; a blank field keeps it.
import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, Dialog, SelectField, TextField, useToast } from '@radish/ui-web';
import { useApi, unwrap, useLocationQuery, type CallFailure } from '../../data/api.tsx';
import { uuidv7 } from '../../lib/ids.ts';
import { refreshSession } from '../../session/session.ts';
import { phoneText } from '../branding/LocationInfoPage.tsx';
import { emailErrors } from '../../lib/email.ts';

/** The key the account dialog's reads are cached under: they belong to no location. */
const PLATFORM = 'platform';


interface Form {
  readonly resellerId: string | null;
  readonly name: string;
  readonly achApiKey: string;
  readonly email: string;
  readonly phone: string;
}

const BLANK: Form = { resellerId: null, name: '', achApiKey: '', email: '', phone: '' };

type Errors = Partial<Record<keyof Form, string>>;

function check(form: Form, pickReseller: boolean): Errors {
  const email = form.email.trim();
  const digits = form.phone.replace(/\D/g, '');
  return {
    ...(pickReseller && form.resellerId === null ? { resellerId: 'Reseller is required' } : {}),
    ...(form.name.trim() === '' ? { name: 'Please type a name' } : {}),
    ...emailErrors(email),
    ...(digits !== '' && digits.length !== 10 ? { phone: 'Phone number must be 10 digits' } : {}),
  };
}

export function AccountDialog({ open, accountId, isAdmin, onClose }: {
  readonly open: boolean;
  /** The account to edit, or null to create one. */
  readonly accountId: string | null;
  /** A Radish admin picks the reseller; a reseller's accounts are always its own (legacy `loggedInAsAdmin`). */
  readonly isAdmin: boolean;
  readonly onClose: () => void;
}) {
  const toast = useToast();
  const { client } = useApi();
  const editing = accountId !== null;
  const resellers = useLocationQuery(PLATFORM, 'tenancy.resellers.list', {}, (c) => c.queries.tenancy.resellers.list({}), open && isAdmin);
  const account = useLocationQuery(PLATFORM, 'tenancy.account', { accountId }, (c) => c.queries.tenancy.account({ accountId: accountId as never }), open && editing);
  const [form, setForm] = useState<Form>(BLANK);
  const [errors, setErrors] = useState<Errors>({});
  // A create keeps one account id per dialog, so a retried attempt replays instead of making a second account.
  const [newId, setNewId] = useState(() => uuidv7());

  const loaded = account.data;
  useEffect(() => {
    if (!open) return;
    if (!editing) setForm(BLANK);
    else if (loaded !== undefined) setForm({ resellerId: loaded.resellerId, name: loaded.name, achApiKey: '', email: loaded.email ?? '', phone: loaded.phone ?? '' });
  }, [open, editing, loaded]);

  const save = useMutation<unknown, CallFailure, Form>({
    mutationFn: (f) => {
      const commandId = uuidv7();
      const base = {
        name: f.name.trim(), email: f.email.trim().toLowerCase(),
        ...(isAdmin && f.resellerId !== null ? { resellerId: f.resellerId } : {}),
        ...(f.achApiKey.trim() === '' ? {} : { achApiKey: f.achApiKey.trim() }),
      };
      const phone = f.phone.replace(/\D/g, '');
      return editing
        ? unwrap(() => client.commands.tenancy.updateAccount({ ...base, accountId: accountId as never, phone: phone === '' ? null : phone }, { commandId }))
        : unwrap(() => client.commands.tenancy.createAccount({ ...base, accountId: newId as never, ...(phone === '' ? {} : { phone }) }, { commandId }));
    },
  });

  const close = () => {
    setForm(BLANK);
    setErrors({});
    save.reset();
    onClose();
  };

  const submit = () => {
    const next = check(form, isAdmin);
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    save.mutate(form, {
      onSuccess: async () => {
        toast.success(editing ? 'Your account has been updated' : 'Account created successfully');
        if (!editing) setNewId(uuidv7());
        // The Accounts list is the session's account list: a refresh brings the new or renamed card.
        await refreshSession();
        close();
      },
      onError: (e) => toast.error(e.message),
    });
  };

  const set = (key: keyof Form) => (value: string) => setForm((f) => ({ ...f, [key]: value }));
  const lastFour = loaded?.achKeyLastFour ?? null;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => { if (!o) close(); }}
      title={editing ? 'Edit Account' : 'Create Account'}
      phoneFooterFill
      testId="account-dialog"
      footer={
        <>
          <Button variant="outline" onClick={close}>Cancel</Button>
          <Button onClick={submit} loading={save.isPending} disabled={editing && loaded === undefined}>{editing ? 'Update' : 'Create'}</Button>
        </>
      }
    >
      <form className="flex flex-col gap-4" autoComplete="off" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        {isAdmin ? (
          <SelectField
            label="Select Reseller"
            value={form.resellerId}
            options={(resellers.data?.resellers ?? []).map((r) => ({ value: r.resellerId, label: r.name }))}
            onValueChange={set('resellerId')}
            error={errors.resellerId}
            testId="account-reseller"
          />
        ) : null}
        <TextField label="Business Name" value={form.name} onValueChange={set('name')} error={errors.name} />
        <TextField
          label="ACH API Key"
          value={form.achApiKey}
          onValueChange={set('achApiKey')}
          autoComplete="off"
          hint={lastFour === null ? undefined : `Key ending in ${lastFour} is saved. Leave blank to keep it.`}
        />
        <TextField label="Email" type="email" value={form.email} onValueChange={set('email')} error={errors.email} />
        <TextField label="Phone" inputMode="tel" value={phoneText(form.phone.replace(/\D/g, '').slice(0, 10))} onValueChange={(v) => set('phone')(v.replace(/\D/g, '').slice(0, 10))} error={errors.phone} />
        <button type="submit" hidden aria-hidden tabIndex={-1} />
      </form>
    </Dialog>
  );
}
